import { ReactNode, useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Check, Mail, CalendarDays, MapPin, Users, Utensils, Music } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SEOHead from "@/components/SEOHead";
import ImageLightbox from "@/components/ImageLightbox";
import { Button } from "@/components/ui/button";
import stephanSos from "@/assets/refugium/stephan-sos.jpg.asset.json";
import meer from "@/assets/refugium/meer.jpg.asset.json";
import horizont from "@/assets/refugium/horizont.jpg.asset.json";
import yoga from "@/assets/refugium/yoga.jpg.asset.json";
import einzelsessions from "@/assets/refugium/einzelsessions.jpg.asset.json";
import fruehstueck from "@/assets/refugium/fruehstueck.jpg.asset.json";
import inesWimmer from "@/assets/refugium/ines-wimmer.jpg.asset.json";
import kulinarik1 from "@/assets/refugium/kulinarik-1.jpg.asset.json";
import kulinarik2 from "@/assets/refugium/kulinarik-2.jpg.asset.json";
import kulinarik3 from "@/assets/refugium/kulinarik-3.jpg.asset.json";
import musikTerrasse from "@/assets/refugium/musik-terrasse.jpg.asset.json";
import ort01 from "@/assets/refugium/ort-01.jpg.asset.json";
import ort02 from "@/assets/refugium/ort-02.jpg.asset.json";
import ort03 from "@/assets/refugium/ort-03.jpg.asset.json";
import ort04 from "@/assets/refugium/ort-04.jpg.asset.json";
import ort05 from "@/assets/refugium/ort-05.jpg.asset.json";
import ort06 from "@/assets/refugium/ort-06.jpg.asset.json";
import ort07 from "@/assets/refugium/ort-07.jpg.asset.json";
import ort08 from "@/assets/refugium/ort-08.jpg.asset.json";
import ort09 from "@/assets/refugium/ort-09.jpg.asset.json";
import ort10 from "@/assets/refugium/ort-10.jpg.asset.json";
import ort11 from "@/assets/refugium/ort-11.jpg.asset.json";
import ort12 from "@/assets/refugium/ort-12.jpg.asset.json";

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.6 } },
};

const Reveal = ({ children, className }: { children: ReactNode; className?: string }) => (
  <motion.div
    initial="hidden"
    whileInView="visible"
    viewport={{ once: true, amount: 0.15 }}
    variants={fadeInUp}
    className={className}
  >
    {children}
  </motion.div>
);

const H2 = ({ children }: { children: ReactNode }) => (
  <h2 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-6">{children}</h2>
);

const fragen = [
  "Wie funktioniert Wandel in Menschen und Organisationen tatsächlich – und was muss stabil bleiben, damit Veränderung gelingt?",
  "Sind die Konzepte von Change und Transformation für die heutige Zeit noch tragfähig, oder braucht es andere Denkmodelle?",
  "Welche Fähigkeiten und welche Haltung brauche ich als Person, um antwortfähig zu bleiben?",
  "Welche Rahmenbedingungen, Interventionen und Werkzeuge helfen, Organisationen und Teams in Bewegung handlungsfähig zu halten?",
];

const ergebnisse = [
  "eine geklärte, tragfähige Haltung und Strategie zu Wandel und Unsicherheit",
  "mehr Spielraum, auf Unvorhergesehenes zu antworten, statt zu erstarren oder sich im Klein-Klein zu verlieren",
  "Klarheit darüber, wie Du dir selbst Sicherheit in unsicheren Zeiten gibst und Deinen Mitarbeitenden einen sicheren Rahmen, um gemeinsam durch Transformation zu gehen",
  "Deine handlungsleitenden Grundannahmen geprüft und dienlichere entwickelt",
  "einen individuellen Strategie- und Führungskompass für die Zeit nach dem Refugium.",
];

const begleiter = [
  {
    name: "Ines Wimmer",
    rolle: "Geschäftsführende Gesellschafterin, Kommunikationslotsen",
    text: "Führungskräftecoach und Organisationsberaterin. Begleitete zuvor als Beraterin Transformationsprozesse in einem globalen Unternehmen, u. a. im arabischen Sprachraum, in Asien und Afrika. Arbeitet auf Basis der Metatheorie der Veränderung und ist Mitautorin der „Lernlandkarte Organisationsklugheit“ (Neuland, 2025).",
    image: inesWimmer.url,
    initials: "IW",
    box: "aspect-square",
    pos: "object-center",
  },
  {
    name: "Stephan Rathgeber",
    rolle: "Geschäftsführer und Berater, Leadership Focus",
    text: "Bringt 15 Jahre Erfahrung in Senior-Führungsrollen mit, u. a. als Geschäftsführer der Neue Fische GmbH, Vice President bei der New Work SE und Leiter des Vorstandsressorts Innovation & Digitalisierung der Hays AG. Systemischer Coach, ausgebildeter Facilitator, Dozent an der HWZ und der FHNW sowie Buchautor.",
    image: stephanSos.url,
    initials: "SR",
    box: "aspect-[4/5]",
    pos: "object-top",
  },
];

const leistungen: [string, string][] = [
  ["Dauer", "5 Tage: Anreise am Montag, Abreise am Freitag"],
  ["Ort", "Villa Sorriso bei Castiglioncello, Toskana"],
  ["Anreise", "Flughafen Pisa (ca. 45 km) oder Florenz (ca. 110 km), alternativ per Zug bis Castiglioncello oder per PKW"],
  ["Teilnehmende", "maximal 5 Personen – für Vertraulichkeit und volle Aufmerksamkeit für Deine Anliegen"],
  ["Leistungen", "Programm mit zwei Berater:innen und Coaches, Unterkunft im eigenen Zimmer mit Bad, Vorgespräch vor dem Refugium"],
  ["Verpflegung", "Erstklassige Vollverpflegung"],
  ["Termin", "1.–5. Februar 2027. Andere Februar-Wochen sind auch möglich"],
  ["Investition", "Auf Anfrage – Kostenrahmen folgt in Kürze"],
];

const galerie = [
  { src: ort01.url, alt: "Luftaufnahme der Villa Sorriso mit Pool, Garten und Meerblick" },
  { src: ort02.url, alt: "Yoga-Plattform aus Holz unter alten Kiefern" },
  { src: ort03.url, alt: "Gedeckter Tisch mit Blick durch das Fenster aufs offene Meer" },
  { src: ort04.url, alt: "Küche der Villa Sorriso mit langem gedecktem Tisch" },
  { src: ort05.url, alt: "Gartentisch unter altem Baum auf dem Grundstück der Villa Sorriso" },
  { src: ort06.url, alt: "Lounge-Sitzecken im Garten der Villa Sorriso mit Meerblick" },
  { src: ort07.url, alt: "Festlich gedeckte Tafel im Garten der Villa Sorriso" },
  { src: ort08.url, alt: "Helles Zimmer mit Meerblick in der Villa Sorriso" },
  { src: ort09.url, alt: "Schlafzimmer mit Balkon und Meerblick" },
  { src: ort10.url, alt: "Küche mit Esstisch in der Villa Sorriso" },
  { src: ort11.url, alt: "Badezimmer mit Dusche und Rundspiegel" },
  { src: ort12.url, alt: "Essbereich der Villa Sorriso mit Esstisch" },
];

const kontakte = [
  { name: "Ines Wimmer", user: "ines.wimmer", domain: "kommunikationslotsen.de" },
  { name: "Stephan Rathgeber", user: "stephan.rathgeber", domain: "leadership-focus.de" },
];

const LeadershipRefugium = () => {
  const runwayRef = useRef<HTMLDivElement>(null);
  const imgRunwayRef = useRef<HTMLDivElement>(null);
  const meerImgRef = useRef<HTMLImageElement>(null);
  const [active, setActive] = useState(0);

  useEffect(() => {
    const el = runwayRef.current;
    if (!el) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const total = el.offsetHeight - window.innerHeight;
        const progress = total > 0 ? Math.min(1, Math.max(0, -el.getBoundingClientRect().top / total)) : 0;
        setActive(Math.min(fragen.length - 1, Math.floor(progress * fragen.length)));

        const imgRunway = imgRunwayRef.current;
        const img = meerImgRef.current;
        if (imgRunway && img) {
          const totalImg = imgRunway.offsetHeight - window.innerHeight;
          const p = totalImg > 0 ? Math.min(1, Math.max(0, -imgRunway.getBoundingClientRect().top / totalImg)) : 0;
          const maxShift = Math.max(0, img.offsetHeight - window.innerHeight);
          img.style.transform = `translateY(${(-p * maxShift).toFixed(1)}px)`;
        }
      });
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
    };
  }, []);

  return (
  <>
    <SEOHead
      title="Leadership-Refugium Toskana – Antwortfähig bleiben | Leadership Focus"
      description="Fünf Tage, fünf Führungskräfte, zwei Begleiter:innen in der Villa Sorriso an der toskanischen Küste. 1.–5. Februar 2027. Für Geschäftsführer:innen, Vorstände und Bereichsleitungen."
      canonical="https://leadership-focus.de/leadership-refugium"
    />
    <Header />
    <main>
      {/* Hero */}
      <section className="relative min-h-[85vh] flex items-end overflow-hidden">
        <img src={meer.url} alt="Blick vom Grundstück der Villa Sorriso auf das offene Mittelmeer" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-foreground/85 via-foreground/40 to-foreground/10" />
        <div className="container relative z-10 pb-16 md:pb-24 pt-32">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="max-w-3xl text-background">
            <p className="uppercase tracking-[0.25em] text-sm mb-4 text-background/80">Leadership-Refugium</p>
            <h1 className="text-5xl md:text-7xl font-serif font-bold mb-6">Antwortfähig bleiben</h1>
            <p className="text-xl md:text-2xl leading-relaxed text-background/90 mb-8">
              Fünf Tage, fünf Führungskräfte, zwei Begleiter:innen – in der Villa Sorriso an der toskanischen Küste.
            </p>
            <div className="flex flex-wrap gap-x-6 gap-y-2 text-background/85 mb-10">
              <span className="inline-flex items-center gap-2"><CalendarDays className="h-4 w-4" /> 1.–5. Februar 2027</span>
              <span className="inline-flex items-center gap-2"><MapPin className="h-4 w-4" /> Castiglioncello, Toskana</span>
              <span className="inline-flex items-center gap-2"><Users className="h-4 w-4" /> max. 5 Teilnehmende</span>
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Button size="lg" onClick={() => document.getElementById("kontakt")?.scrollIntoView({ behavior: "smooth" })}>
                Platz anfragen
              </Button>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Worum es geht */}
      <section className="py-20 md:py-28">
        <div className="container grid gap-12 lg:grid-cols-5">
          <Reveal className="lg:col-span-3 space-y-5 text-lg text-muted-foreground leading-relaxed">
            <H2>Worum es geht</H2>
            <p>
              Transformation ist für viele Organisationen kein Projekt mit Anfang und Ende mehr, sondern Dauerzustand. Wer an der Spitze Verantwortung trägt, steht vor einer zentralen Frage: Wie bleibe ich handlungsfähig, wenn sich die Welt schneller verändert, als sich Pläne schreiben lassen? Und wie gebe ich meinen Mitarbeitenden Halt, ohne selbst alle Antworten zu haben?
            </p>
            <p>
              Dieses Refugium lädt Dich ein, aus dem Alltag herauszutreten und genau diese Fragen zu bearbeiten – im kleinen Kreis, auf Augenhöhe und mit der Zeit, die im Tagesgeschäft fehlt.
            </p>
          </Reveal>
          <Reveal className="lg:col-span-2">
            <div className="rounded-2xl bg-primary/5 border border-primary/20 p-8 h-full">
              <h3 className="text-2xl font-serif font-semibold text-foreground mb-4">Für wen?</h3>
              <p className="text-muted-foreground leading-relaxed">
                Geschäftsführer:innen, Vorstände, Bereichsleitungen und besondere Menschen, die Organisationen durch Veränderung führen und ihre eigene Führungshaltung grundlegend schärfen möchten.
              </p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* Panorama */}
      <section className="relative h-[45vh] min-h-[320px] md:h-[60vh] overflow-hidden">
        <img src={horizont.url} alt="Blick über das Meer von der Villa Sorriso" className="absolute inset-0 h-full w-full object-cover" />
      </section>

      {/* Was Dich erwartet */}
      <section className="bg-muted/40">
        <div className="container pt-20 pb-10 md:pt-28">
          <Reveal className="mx-auto max-w-3xl text-center">
            <H2>Was Dich erwartet</H2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Im Zentrum stehen exklusive Austauschrunden, in denen wir zwei Berater:innen offen unsere Perspektiven auf Wandel teilen. Nicht als fertige Rezepte, sondern als Denkangebote, die wir gemeinsam mit Dir prüfen und weiterentwickeln. Dabei gehen wir Fragen nach wie:
            </p>
          </Reveal>
        </div>

        <div ref={runwayRef} className="relative h-[300vh]">
          <div className="sticky top-0 flex h-screen w-full items-center bg-primary">
              <div className="container grid">
                {fragen.map((f, i) => (
                  <p
                    key={i}
                    aria-hidden={active !== i}
                    className={`col-start-1 row-start-1 self-center text-center font-serif text-2xl leading-snug text-primary-foreground transition-all duration-700 ease-out md:text-3xl lg:text-4xl ${
                      active === i ? "opacity-100" : "opacity-0"
                    }`}
                  >
                    {f}
                  </p>
                ))}
              </div>
            </div>
          </div>

        <div ref={imgRunwayRef} className="relative h-[200vh]">
          <div className="sticky top-0 h-screen w-full overflow-hidden bg-foreground">
            <img
              ref={meerImgRef}
              src={meer.url}
              alt="Blick vom Grundstück der Villa Sorriso auf das offene Mittelmeer"
              className="absolute inset-x-0 top-0 w-full min-h-screen object-cover will-change-transform"
            />
            <div className="absolute inset-0 bg-foreground/55" />
            <div className="relative flex h-full items-center justify-center">
              <p className="mx-auto max-w-3xl px-6 text-center text-xl leading-relaxed text-primary-foreground md:text-2xl">
                Deine eigenen Führungssituationen sind dabei das wichtigste Material. Theorie wird dort lebendig, wo sie an realen Fragen geprüft wird.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Einzelsessions */}
      <section className="pt-10 pb-20 md:pt-14 md:pb-28">
        <div className="container grid gap-12 lg:grid-cols-2 items-center">
          <Reveal>
            <ImageLightbox src={einzelsessions.url} alt="Für zwei gedeckter Tisch auf dem Balkon mit Blick über das Meer" className="w-full aspect-[4/3] object-cover rounded-2xl" />
          </Reveal>
          <Reveal className="space-y-5 text-lg text-muted-foreground leading-relaxed">
            <H2>Einzelsessions nach Wahl</H2>
            <p>
              Ergänzend kannst Du während des Refugiums individuelle Sessions bei einem systemischen Coach und Berater Deiner Wahl aus unserem Team buchen – als Führungscoaching, Sparring oder zur Persönlichkeitsentwicklung. Hier arbeitest Du vertraulich an Deinen eigenen Anliegen und schärfst Deine Führungspersönlichkeit und Antwortfähigkeit gezielt weiter.
            </p>
            <p>
              Zwischen den Arbeitsphasen bleibt bewusst Raum: für Gespräche beim Essen, den Blick übers Meer, Stille auf der Meditationsterrasse und das Nachwirken lassen dessen, was in Bewegung gekommen ist.
            </p>
          </Reveal>
        </div>
      </section>

      {/* Was Du mitnehmen wirst */}
      <section className="py-20 md:py-28 bg-primary text-primary-foreground">
        <div className="container max-w-4xl">
          <Reveal>
            <h2 className="text-3xl md:text-4xl font-serif font-bold mb-8">Was Du mitnehmen wirst</h2>
            <ul className="space-y-4">
              {ergebnisse.map((e) => (
                <li key={e} className="flex gap-4 text-lg leading-relaxed">
                  <Check className="h-6 w-6 shrink-0 mt-0.5" />
                  <span>{e}</span>
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* Begleiter */}
      <section className="py-20 md:py-28">
        <div className="container">
          <Reveal className="max-w-3xl mb-12">
            <H2>Deine Begleiter:innen</H2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Zwei unterschiedliche Blickwinkel auf Wandel: die Begleitung von Organisationen und Menschen – und die eigene Verantwortung an der Spitze von Unternehmen.
            </p>
          </Reveal>
          <div className="space-y-16 md:space-y-24">
            {begleiter.map((b, i) => (
              <Reveal key={b.name}>
                <div className="grid gap-8 md:grid-cols-2 md:gap-12 items-center">
                  {b.image ? (
                    <img
                      src={b.image}
                      alt={b.name}
                      className={`w-full ${b.box} ${b.pos} object-cover rounded-2xl ${i % 2 === 1 ? "md:order-2" : ""}`}
                    />
                  ) : (
                    <div className={`w-full ${b.box} rounded-2xl bg-primary/10 text-primary flex items-center justify-center font-serif text-6xl ${i % 2 === 1 ? "md:order-2" : ""}`}>
                      {b.initials}
                    </div>
                  )}
                  <div>
                    <h3 className="text-2xl md:text-3xl font-serif font-semibold text-foreground">{b.name}</h3>
                    <p className="text-base text-primary italic mt-1 mb-5">{b.rolle}</p>
                    <p className="text-lg text-muted-foreground leading-relaxed">{b.text}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Der Ort */}
      <section className="py-20 md:py-28 bg-muted/40">
        <div className="container">
          <Reveal className="max-w-3xl mb-12 space-y-5 text-lg text-muted-foreground leading-relaxed">
            <H2>Der Ort</H2>
            <p>
              Die Villa Sorriso liegt auf einem eigenen, geschlossenen Grundstück an der toskanischen Küste zwischen Livorno und Cecina, mit weitem Blick auf das Mittelmeer. Das 2021 renovierte Haus ist hell und modern eingerichtet; jede:r Teilnehmende hat ein eigenes Zimmer mit Bad.
            </p>
            <p>
              Gearbeitet wird drinnen und draußen: an langen Tischen unter alten Kiefern, auf einer Holzplattform für Yoga und Meditation, im Garten mit Pool. Ein Ort, der Abstand zum Alltag schafft und Raum für das, was im Tagesgeschäft keinen Platz hat.
            </p>
          </Reveal>
          <div className="grid gap-4 grid-cols-2 lg:grid-cols-4">
            {galerie.map((g) => (
              <Reveal key={g.src}>
                <ImageLightbox src={g.src} alt={g.alt} className="w-full aspect-square object-cover rounded-xl" />
              </Reveal>
            ))}
          </div>
          <Reveal className="mt-10 max-w-3xl">
            <p className="text-lg text-muted-foreground leading-relaxed">
              Weitere Infos zu unserem Refugium findest Du{" "}
              <a
                href="https://villa-sorriso-tuscany.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium text-primary underline-offset-4 hover:underline"
              >
                hier
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      {/* Rahmenprogramm */}
      <section className="py-20 md:py-28">
        <div className="container">
          <Reveal className="max-w-3xl mb-12">
            <H2>Rahmenprogramm</H2>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Gute Gespräche brauchen gute Orte und Momente, in denen nicht gearbeitet wird. Kulinarik und Musik sind deshalb fester Teil des Refugiums.
            </p>
          </Reveal>
          <div className="grid gap-8 md:grid-cols-2">
            <Reveal>
              <div className="h-full rounded-2xl border border-border p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Utensils className="h-7 w-7 shrink-0 text-primary" />
                  <h3 className="text-2xl font-serif font-semibold text-foreground">Kulinarische Highlights</h3>
                </div>
                <ul className="space-y-3 text-muted-foreground leading-relaxed list-disc pl-5">
                  <li>Aperitivo bei Sonnenuntergang auf der Terrasse über dem Meer</li>
                  <li>Abendessen mit toskanischer Küche aus regionalen Zutaten, an langen Tischen unter alten Kiefern</li>
                  <li>Ein gemeinsamer Kochabend mit einer Köchin oder einem Koch aus der Region</li>
                  <li>Weinverkostung mit Weinen der toskanischen Küste, z. B. aus dem nahen Bolgheri</li>
                </ul>
                <div className="mt-6 grid grid-cols-3 gap-3">
                  <ImageLightbox src={kulinarik1.url} alt="Küche mit Holzbackofen und Brotzeit" className="w-full aspect-square object-cover rounded-xl" />
                  <ImageLightbox src={kulinarik2.url} alt="Küche der Villa Sorriso mit gedecktem Tisch" className="w-full aspect-square object-cover rounded-xl" />
                  <ImageLightbox src={kulinarik3.url} alt="Kamin mit Bruschetta und Basilikum" className="w-full aspect-square object-cover rounded-xl" />
                </div>
              </div>
            </Reveal>
            <Reveal>
              <div className="h-full rounded-2xl border border-border p-8">
                <div className="flex items-center gap-3 mb-4">
                  <Music className="h-7 w-7 shrink-0 text-primary" />
                  <h3 className="text-2xl font-serif font-semibold text-foreground">Musikalische Highlights</h3>
                </div>
                <p className="text-muted-foreground leading-relaxed">
                  Musik als Resonanzraum: ein Abend, an dem Klang statt Worte im Mittelpunkt steht. Ein privates Abendkonzert auf der Terrasse, z. B. mit Kammermusik, DJ oder einem Jazz-Duo, und eine kleine Tanzeinlage.
                </p>
                <ImageLightbox src={musikTerrasse.url} alt="Terrasse unter altem Kiefer mit Blick aufs Meer" className="mt-6 w-full aspect-[16/9] object-cover rounded-xl" />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Rahmen und Leistungen */}
      <section className="py-20 md:py-28 bg-muted/40">
        <div className="container max-w-4xl">
          <Reveal>
            <H2>Rahmen und Leistungen</H2>
            <dl className="rounded-2xl bg-background border border-border divide-y divide-border">
              {leistungen.map(([k, v]) => (
                <div key={k} className="grid sm:grid-cols-3 gap-2 p-5">
                  <dt className="font-semibold text-foreground">{k}</dt>
                  <dd className="sm:col-span-2 text-muted-foreground">{v}</dd>
                </div>
              ))}
            </dl>
          </Reveal>
        </div>
      </section>

      {/* Kontakt */}
      <section id="kontakt" className="relative py-24 md:py-32 overflow-hidden scroll-mt-14">
        <img src={fruehstueck.url} alt="" aria-hidden className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-foreground/70" />
        <div className="container relative z-10 max-w-3xl text-center text-background">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-serif font-bold mb-6">Anmeldung und Kontakt</h2>
            <p className="text-lg leading-relaxed text-background/90 mb-10">
              Die Plätze werden nach einem persönlichen Vorgespräch vergeben. Wir freuen uns auf Deine Nachricht und auf ein erstes Gespräch, in dem wir klären, ob das Refugium zu Deiner Situation passt.
            </p>
            <div className="grid gap-6 sm:grid-cols-2 mb-10">
              {kontakte.map((c) => (
                <div key={c.name} className="rounded-xl bg-background/10 backdrop-blur p-6 border border-background/20">
                  <p className="font-serif text-xl mb-2">{c.name}</p>
                  <a href={`mailto:${c.user}@${c.domain}`} className="inline-flex items-center gap-2 text-background/85 hover:text-background">
                    <Mail className="h-4 w-4" /> {c.user} (at) {c.domain}
                  </a>
                </div>
              ))}
            </div>
            <Button size="lg" asChild>
              <a href="mailto:stephan.rathgeber@leadership-focus.de,ines.wimmer@kommunikationslotsen.de?subject=Leadership-Refugium%20%E2%80%93%20Vorgespr%C3%A4ch">
                Vorgespräch anfragen
              </a>
            </Button>
          </Reveal>
        </div>
      </section>
    </main>
    <Footer />
  </>
  );
};

export default LeadershipRefugium;
